import * as React from 'react';

import { cva } from 'class-variance-authority';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import {
    type DayButton,
    DayPicker,
    getDefaultClassNames,
    type Locale,
    type Matcher,
} from 'react-day-picker';

import { cn } from '../lib/utils';
import { Button, buttonVariants } from './button';

const calendarCellSizeVariants = cva('', {
    variants: {
        cellSize: {
            default: '[--cell-size:--spacing(7)]',
            md: '[--cell-size:--spacing(8)]',
            lg: '[--cell-size:--spacing(12)]',
        },
    },
    defaultVariants: { cellSize: 'default' },
});

const calendarDayCellVariants = cva(
    'group/day relative rounded-(--cell-radius) p-0 text-center select-none [&:last-child[data-selected=true]_button]:rounded-r-(--cell-radius)',
    {
        variants: {
            cellSize: {
                default: 'aspect-square h-full w-full',
                md: 'flex flex-1 items-center justify-center',
                // `min-w-0` lets the column shrink below the button's 48 px under 360 px.
                lg: 'flex min-w-0 flex-1 items-center justify-center',
            },
        },
        defaultVariants: { cellSize: 'default' },
    },
);

const calendarDayButtonSizeVariants = cva('', {
    variants: {
        cellSize: {
            default: 'aspect-square size-auto w-full min-w-(--cell-size)',
            md: 'size-(--cell-size)',
            // Figma trims the digit's text box to its caps, so the 2px gap below reads as the
            // same 4px Figma shows between the untrimmed digit and the dot row.
            // Shrinks with its column below 360 px and stays square; `size-auto` cancels `Button`'s `size-9`.
            lg: 'aspect-square size-auto w-full max-w-(--cell-size) gap-0.5',
        },
    },
    defaultVariants: { cellSize: 'default' },
});

const calendarDayMarkerRowVariants = cva('flex items-center justify-center', {
    variants: {
        cellSize: {
            default: 'h-1',
            md: 'h-1',
            lg: 'h-3',
        },
    },
    defaultVariants: { cellSize: 'default' },
});

const calendarWeekdayVariants = cva(
    'flex-1 rounded-(--cell-radius) font-normal text-muted-foreground select-none',
    {
        variants: {
            cellSize: {
                default: 'text-[0.8rem]',
                md: 'flex h-5.25 items-center justify-center text-xs',
                lg: 'flex h-5.25 items-center justify-center text-xs',
            },
        },
        defaultVariants: { cellSize: 'default' },
    },
);

// `md`/`lg` buttons are a fixed `--cell-size` square inside a wider `flex-1` cell, so today's
// colour goes on the button there (`calendarTodayButtonVariants` below) and on the `td` for `default`.
const calendarTodayVariants = cva('rounded-(--cell-radius) data-[selected=true]:rounded-none', {
    variants: {
        cellSize: {
            default: '',
            md: '',
            lg: '',
        },
        dayStyle: {
            default: '',
            accent: '',
        },
    },
    compoundVariants: [
        { cellSize: 'default', dayStyle: 'default', class: 'bg-muted text-foreground' },
        { cellSize: 'default', dayStyle: 'accent', class: 'bg-accent text-accent-foreground' },
    ],
    defaultVariants: { cellSize: 'default', dayStyle: 'default' },
});

// Selected wins over today: `CalendarDayButton` only spreads this in when the day is not
// selected, so precedence is a plain `if`, not two same-specificity classes racing in the
// stylesheet.
const calendarTodayButtonVariants = cva('', {
    variants: {
        cellSize: {
            default: '',
            md: '',
            lg: '',
        },
        dayStyle: {
            default: '',
            accent: '',
        },
    },
    compoundVariants: [
        { cellSize: 'md', dayStyle: 'default', class: 'bg-muted text-foreground' },
        { cellSize: 'md', dayStyle: 'accent', class: 'bg-accent text-accent-foreground' },
        { cellSize: 'lg', dayStyle: 'default', class: 'bg-muted text-foreground' },
        { cellSize: 'lg', dayStyle: 'accent', class: 'bg-accent text-accent-foreground' },
    ],
    defaultVariants: { cellSize: 'default', dayStyle: 'default' },
});

const calendarOutsideVariants = cva('text-muted-foreground aria-selected:text-muted-foreground', {
    variants: {
        dayStyle: {
            default: '',
            accent: 'opacity-50',
        },
    },
    defaultVariants: { dayStyle: 'default' },
});

// `Button`'s ghost variant sets `text-foreground` on the button itself, which overrides the
// outside `td`'s `text-muted-foreground` above — so an outside day under `accent` needs its own
// colour on the button.
const calendarOutsideButtonVariants = cva('', {
    variants: {
        dayStyle: {
            default: '',
            accent: 'text-muted-foreground',
        },
    },
    defaultVariants: { dayStyle: 'default' },
});

const calendarNavButtonVariants = cva(
    'size-(--cell-size) p-0 select-none aria-disabled:opacity-50',
    {
        variants: {
            dayStyle: {
                default: '',
                accent: 'opacity-50',
            },
        },
        defaultVariants: { dayStyle: 'default' },
    },
);

// Module-level: an inline `String.raw` makes React Compiler skip `Calendar`, which then remounts
// on every render. Keep `\_` (not `\\_`) or Tailwind stops generating the rotate class.
const RTL_NEXT_CHEVRON_ROTATE_CLASS = String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`;
const RTL_PREVIOUS_CHEVRON_ROTATE_CLASS = String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`;

function Calendar({
    className,
    classNames,
    showOutsideDays = true,
    captionLayout = 'label',
    buttonVariant = 'ghost',
    cellSize = 'default',
    dayStyle = 'default',
    locale,
    formatters,
    components,
    marked,
    modifiers,
    hideCaption = false,
    ...props
}: React.ComponentProps<typeof DayPicker> & {
    buttonVariant?: React.ComponentProps<typeof Button>['variant'];
    marked?: Matcher | Matcher[];
    // react-day-picker's `hideNavigation` hides only the arrows; this hides the caption label
    // itself while keeping it in the accessible tree, for a picker whose current month is named
    // elsewhere on the page.
    hideCaption?: boolean;
    // Pure geometry: the token row/column/weekday sizing keys off — `md` is the sidebar's 32px
    // cell, `lg` the mobile picker's 48px cell.
    cellSize?: 'default' | 'md' | 'lg';
    // Pure color/opacity for today/outside/nav-arrow styling — independent of cellSize.
    dayStyle?: 'default' | 'accent';
}) {
    const defaultClassNames = getDefaultClassNames();
    const markable = marked !== undefined;

    return (
        <DayPicker
            showOutsideDays={showOutsideDays}
            modifiers={marked === undefined ? modifiers : { ...modifiers, marked }}
            className={cn(
                'group/calendar bg-transparent p-2 [--cell-radius:var(--radius-md)] in-data-[slot=card-content]:bg-transparent in-data-[slot=popover-content]:bg-transparent',
                calendarCellSizeVariants({ cellSize }),
                RTL_NEXT_CHEVRON_ROTATE_CLASS,
                RTL_PREVIOUS_CHEVRON_ROTATE_CLASS,
                className,
            )}
            captionLayout={captionLayout}
            locale={locale}
            formatters={{
                formatMonthDropdown: (date) =>
                    date.toLocaleString(locale?.code, { month: 'short' }),
                ...formatters,
            }}
            classNames={{
                root: cn('w-fit', defaultClassNames.root),
                months: cn('relative flex flex-col gap-4 md:flex-row', defaultClassNames.months),
                month: cn('flex w-full flex-col gap-4', defaultClassNames.month),
                nav: cn(
                    'absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1',
                    defaultClassNames.nav,
                ),
                button_previous: cn(
                    buttonVariants({ variant: buttonVariant }),
                    calendarNavButtonVariants({ dayStyle }),
                    defaultClassNames.button_previous,
                ),
                button_next: cn(
                    buttonVariants({ variant: buttonVariant }),
                    calendarNavButtonVariants({ dayStyle }),
                    defaultClassNames.button_next,
                ),
                month_caption: hideCaption
                    ? cn('sr-only', defaultClassNames.month_caption)
                    : cn(
                          'flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)',
                          defaultClassNames.month_caption,
                      ),
                dropdowns: cn(
                    'flex h-(--cell-size) w-full items-center justify-center gap-1.5 text-sm font-medium',
                    defaultClassNames.dropdowns,
                ),
                dropdown_root: cn(
                    'cn-calendar-dropdown-root relative rounded-(--cell-radius)',
                    defaultClassNames.dropdown_root,
                ),
                dropdown: cn('absolute inset-0 bg-popover opacity-0', defaultClassNames.dropdown),
                caption_label: cn(
                    'font-medium select-none',
                    captionLayout === 'label'
                        ? 'cn-calendar-caption text-sm'
                        : 'cn-calendar-caption-label flex items-center gap-1 rounded-(--cell-radius) text-sm [&>svg]:size-3.5 [&>svg]:text-muted-foreground',
                    defaultClassNames.caption_label,
                ),
                table: 'w-full border-collapse',
                weekdays: cn('flex', defaultClassNames.weekdays),
                weekday: cn(calendarWeekdayVariants({ cellSize }), defaultClassNames.weekday),
                week: cn('mt-2 flex w-full', defaultClassNames.week),
                week_number_header: cn(
                    'w-(--cell-size) select-none',
                    defaultClassNames.week_number_header,
                ),
                week_number: cn(
                    'text-[0.8rem] text-muted-foreground select-none',
                    defaultClassNames.week_number,
                ),
                day: cn(
                    calendarDayCellVariants({ cellSize }),
                    props.showWeekNumber
                        ? '[&:nth-child(2)[data-selected=true]_button]:rounded-l-(--cell-radius)'
                        : '[&:first-child[data-selected=true]_button]:rounded-l-(--cell-radius)',
                    defaultClassNames.day,
                ),
                range_start: cn(
                    'relative isolate z-0 rounded-l-(--cell-radius) bg-muted after:absolute after:inset-y-0 after:right-0 after:w-4 after:bg-muted',
                    defaultClassNames.range_start,
                ),
                range_middle: cn('rounded-none', defaultClassNames.range_middle),
                range_end: cn(
                    'relative isolate z-0 rounded-r-(--cell-radius) bg-muted after:absolute after:inset-y-0 after:left-0 after:w-4 after:bg-muted',
                    defaultClassNames.range_end,
                ),
                today: cn(calendarTodayVariants({ cellSize, dayStyle }), defaultClassNames.today),
                outside: cn(calendarOutsideVariants({ dayStyle }), defaultClassNames.outside),
                disabled: cn('text-muted-foreground opacity-50', defaultClassNames.disabled),
                hidden: cn('invisible', defaultClassNames.hidden),
                ...classNames,
            }}
            components={{
                Root: ({ className, rootRef, ...props }) => {
                    return (
                        <div
                            data-slot="calendar"
                            ref={rootRef}
                            className={cn(className)}
                            {...props}
                        />
                    );
                },
                Chevron: ({ className, orientation, ...props }) => {
                    if (orientation === 'left') {
                        return (
                            <ChevronLeftIcon
                                className={cn('cn-rtl-flip size-4', className)}
                                {...props}
                            />
                        );
                    }

                    if (orientation === 'right') {
                        return (
                            <ChevronRightIcon
                                className={cn('cn-rtl-flip size-4', className)}
                                {...props}
                            />
                        );
                    }

                    return <ChevronDownIcon className={cn('size-4', className)} {...props} />;
                },
                DayButton: ({ ...props }) => (
                    <CalendarDayButton
                        locale={locale}
                        markable={markable}
                        cellSize={cellSize}
                        dayStyle={dayStyle}
                        {...props}
                    />
                ),
                WeekNumber: ({ children, ...props }) => {
                    return (
                        <td {...props}>
                            <div className="flex size-(--cell-size) items-center justify-center text-center">
                                {children}
                            </div>
                        </td>
                    );
                },
                ...components,
            }}
            {...props}
        />
    );
}

function CalendarDayButton({
    className,
    day,
    modifiers,
    locale,
    markable,
    cellSize = 'default',
    dayStyle = 'default',
    children,
    ...props
}: React.ComponentProps<typeof DayButton> & {
    locale?: Partial<Locale>;
    markable?: boolean;
    cellSize?: 'default' | 'md' | 'lg';
    dayStyle?: 'default' | 'accent';
}) {
    const defaultClassNames = getDefaultClassNames();

    const ref = React.useRef<HTMLButtonElement>(null);
    React.useEffect(() => {
        if (modifiers.focused) ref.current?.focus();
    }, [modifiers.focused]);

    const isSelectedSingle =
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle;
    // The selected fill and the marker dot are both `bg-primary`, so a dot on a selected day is
    // invisible; range_middle keeps its dot since its fill is `bg-muted`, not `bg-primary`.
    const hidesMarkerDot = isSelectedSingle || modifiers.range_start || modifiers.range_end;

    return (
        <Button
            ref={ref}
            variant="ghost"
            size="icon"
            data-day={day.date.toLocaleDateString(locale?.code)}
            data-selected-single={isSelectedSingle}
            data-range-start={modifiers.range_start}
            data-range-end={modifiers.range_end}
            data-range-middle={modifiers.range_middle}
            className={cn(
                'relative isolate z-10 flex flex-col gap-1 border-0 leading-none font-normal group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-[3px] group-data-[focused=true]/day:ring-ring/50 data-[range-end=true]:rounded-(--cell-radius) data-[range-end=true]:rounded-r-(--cell-radius) data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-muted data-[range-middle=true]:text-foreground data-[range-start=true]:rounded-(--cell-radius) data-[range-start=true]:rounded-l-(--cell-radius) data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground dark:hover:text-foreground [&>span]:text-xs [&>span]:opacity-70',
                calendarDayButtonSizeVariants({ cellSize }),
                modifiers.outside && calendarOutsideButtonVariants({ dayStyle }),
                modifiers.today &&
                    !modifiers.selected &&
                    calendarTodayButtonVariants({ cellSize, dayStyle }),
                defaultClassNames.day,
                className,
            )}
            {...props}
        >
            {children}
            {markable && (
                // A `div`, not a `span` — the day's own `[&>span]:text-xs [&>span]:opacity-70`
                // styling targets direct `span` children and would otherwise dim this row too.
                <div aria-hidden="true" className={calendarDayMarkerRowVariants({ cellSize })}>
                    {modifiers.marked && !hidesMarkerDot && (
                        <span
                            data-slot="calendar-day-marker"
                            className="size-1 rounded-full bg-primary"
                        />
                    )}
                </div>
            )}
        </Button>
    );
}

export { Calendar, CalendarDayButton };
